import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatCurrency } from "../config/siteConfig";
import "../css/checkout.css";

const initialForm = {
  email: "",
  country: "HK",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  address2: "",
  city: "",
  region: "",
  postalCode: "",
};

const checkoutFieldIds = {
  email: "checkout-email",
  country: "checkout-country",
  firstName: "checkout-first-name",
  lastName: "checkout-last-name",
  address: "checkout-address",
  city: "checkout-city",
  region: "checkout-region",
  postalCode: "checkout-postal-code",
};

const toNumber = (value) => {
  const parsedValue = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const validateCheckout = (form) => {
  const errors = {};

  if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
    errors.email = "Enter a valid email address.";
  }
  if (!form.country) errors.country = "Select a country or region.";
  if (!form.firstName.trim()) errors.firstName = "Enter your first name.";
  if (!form.lastName.trim()) errors.lastName = "Enter your last name.";
  if (!form.address.trim()) errors.address = "Enter a street address.";
  if (!form.city.trim()) errors.city = "Enter a city.";
  if (!form.region.trim()) errors.region = "Enter a state or region.";
  if (!form.postalCode.trim()) errors.postalCode = "Enter a postal code.";

  return errors;
};

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cartLines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find(
            (product) => String(product.id) === String(item.id)
          ),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = useMemo(
    () =>
      cartLines.reduce(
        (total, item) =>
          total + toNumber(item.product.price) * item.quantity,
        0
      ),
    [cartLines]
  );

  const shipping = toNumber(siteConfig.flatShippingRate);
  const total = subtotal + shipping;

  const updateField = (name, value) => {
    setForm((currentForm) => ({ ...currentForm, [name]: value }));
    if (errors[name]) {
      setErrors((currentErrors) => ({ ...currentErrors, [name]: "" }));
    }
  };

  const handleChange = (event) => {
    updateField(event.target.name, event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isSubmitting || !cartLines.length) return;

    const validationErrors = validateCheckout(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) {
      const firstErrorName = Object.keys(validationErrors)[0];
      const focusFirstError = () => {
        const firstErrorField = document.getElementById(
          checkoutFieldIds[firstErrorName]
        );
        const errorSummary = document.getElementById("checkout-error-summary");
        (firstErrorField || errorSummary)?.focus();
      };

      if (
        typeof window !== "undefined" &&
        typeof window.requestAnimationFrame === "function"
      ) {
        window.requestAnimationFrame(focusFirstError);
      } else if (typeof document !== "undefined") {
        focusFirstError();
      }
      return;
    }

    setIsSubmitting(true);
    const orderNumber = `${Date.now()}${Math.floor(100000 + Math.random() * 900000)}`;
    const origin = window.location.origin;
    const paymentParams = new URLSearchParams({
      orderNo: orderNumber,
      amount: total.toFixed(2),
      currency: siteConfig.currency,
      email: form.email.trim(),
      success_url: `${origin}/payment?state=success`,
      cancel_url: `${origin}/payment?state=cancel`,
      title: cartLines[0].product.name,
    });
    window.location.assign(`${origin}/create_payment.php?${paymentParams.toString()}`);
  };

  if (!cartLines.length) {
    return (
      <main className="checkout-page checkout-page--empty">
        <section className="checkout-empty" aria-labelledby="checkout-empty-title">
          <p className="checkout-page__eyebrow">Checkout</p>
          <h1 id="checkout-empty-title">Your cart is empty.</h1>
          <p>Add something useful to your desk before continuing.</p>
          <Link to="/shop">Browse the collection</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <header className="checkout-page__header">
        <Link to="/cartpage" className="checkout-page__back-link">
          ← Return to cart
        </Link>
        <p className="checkout-page__eyebrow">Secure checkout</p>
        <h1>Complete your order</h1>
        <p>{siteConfig.checkout?.secureMessage}</p>
      </header>

      <div className="checkout-layout">
        <form
          className="checkout-form"
          onSubmit={handleSubmit}
          noValidate
          aria-label="Checkout details"
          aria-describedby={
            Object.values(errors).some(Boolean)
              ? "checkout-error-summary"
              : undefined
          }
        >
          {Object.values(errors).some(Boolean) && (
            <div
              id="checkout-error-summary"
              className="checkout-form__error-summary"
              role="alert"
              tabIndex="-1"
            >
              Please review the highlighted fields before placing your order.
            </div>
          )}

          <fieldset>
            <legend>Contact</legend>
            <div className="checkout-field">
              <label htmlFor="checkout-email">Email address</label>
              <input
                id="checkout-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "checkout-email-error" : undefined}
              />
              {errors.email && (
                <span id="checkout-email-error" className="checkout-field__error">
                  {errors.email}
                </span>
              )}
            </div>
          </fieldset>

          <fieldset>
            <legend>Delivery address</legend>
            <div className="checkout-field">
              <label htmlFor="checkout-country">Country or region</label>
              <select
                id="checkout-country"
                name="country"
                value={form.country}
                onChange={handleChange}
                autoComplete="country"
                aria-invalid={Boolean(errors.country)}
                aria-describedby={
                  errors.country ? "checkout-country-error" : undefined
                }
              >
                <option value="HK">Hong Kong</option>
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="GB">United Kingdom</option>
                <option value="AU">Australia</option>
                <option value="JP">Japan</option>
              </select>
              {errors.country && (
                <span
                  id="checkout-country-error"
                  className="checkout-field__error"
                >
                  {errors.country}
                </span>
              )}
            </div>

            <div className="checkout-form__row">
              <div className="checkout-field">
                <label htmlFor="checkout-first-name">First name</label>
                <input
                  id="checkout-first-name"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  autoComplete="given-name"
                  aria-invalid={Boolean(errors.firstName)}
                  aria-describedby={
                    errors.firstName
                      ? "checkout-first-name-error"
                      : undefined
                  }
                />
                {errors.firstName && (
                  <span
                    id="checkout-first-name-error"
                    className="checkout-field__error"
                  >
                    {errors.firstName}
                  </span>
                )}
              </div>
              <div className="checkout-field">
                <label htmlFor="checkout-last-name">Last name</label>
                <input
                  id="checkout-last-name"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  autoComplete="family-name"
                  aria-invalid={Boolean(errors.lastName)}
                  aria-describedby={
                    errors.lastName ? "checkout-last-name-error" : undefined
                  }
                />
                {errors.lastName && (
                  <span
                    id="checkout-last-name-error"
                    className="checkout-field__error"
                  >
                    {errors.lastName}
                  </span>
                )}
              </div>
            </div>

            <div className="checkout-field">
              <label htmlFor="checkout-company">
                Company <span>(optional)</span>
              </label>
              <input
                id="checkout-company"
                name="company"
                value={form.company}
                onChange={handleChange}
                autoComplete="organization"
              />
            </div>

            <div className="checkout-field">
              <label htmlFor="checkout-address">Street address</label>
              <input
                id="checkout-address"
                name="address"
                value={form.address}
                onChange={handleChange}
                autoComplete="address-line1"
                aria-invalid={Boolean(errors.address)}
                aria-describedby={
                  errors.address ? "checkout-address-error" : undefined
                }
              />
              {errors.address && (
                <span
                  id="checkout-address-error"
                  className="checkout-field__error"
                >
                  {errors.address}
                </span>
              )}
            </div>

            <div className="checkout-field">
              <label htmlFor="checkout-address-2">
                Apartment, suite, etc. <span>(optional)</span>
              </label>
              <input
                id="checkout-address-2"
                name="address2"
                value={form.address2}
                onChange={handleChange}
                autoComplete="address-line2"
              />
            </div>

            <div className="checkout-form__row checkout-form__row--three">
              <div className="checkout-field">
                <label htmlFor="checkout-city">City</label>
                <input
                  id="checkout-city"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  autoComplete="address-level2"
                  aria-invalid={Boolean(errors.city)}
                  aria-describedby={
                    errors.city ? "checkout-city-error" : undefined
                  }
                />
                {errors.city && (
                  <span
                    id="checkout-city-error"
                    className="checkout-field__error"
                  >
                    {errors.city}
                  </span>
                )}
              </div>
              <div className="checkout-field">
                <label htmlFor="checkout-region">State / region</label>
                <input
                  id="checkout-region"
                  name="region"
                  value={form.region}
                  onChange={handleChange}
                  autoComplete="address-level1"
                  aria-invalid={Boolean(errors.region)}
                  aria-describedby={
                    errors.region ? "checkout-region-error" : undefined
                  }
                />
                {errors.region && (
                  <span
                    id="checkout-region-error"
                    className="checkout-field__error"
                  >
                    {errors.region}
                  </span>
                )}
              </div>
              <div className="checkout-field">
                <label htmlFor="checkout-postal-code">Postal code</label>
                <input
                  id="checkout-postal-code"
                  name="postalCode"
                  value={form.postalCode}
                  onChange={handleChange}
                  autoComplete="postal-code"
                  aria-invalid={Boolean(errors.postalCode)}
                  aria-describedby={
                    errors.postalCode
                      ? "checkout-postal-code-error"
                      : undefined
                  }
                />
                {errors.postalCode && (
                  <span
                    id="checkout-postal-code-error"
                    className="checkout-field__error"
                  >
                    {errors.postalCode}
                  </span>
                )}
              </div>
            </div>

            <div className="checkout-shipping-method">
              <span>
                <strong>Standard shipping</strong>
                <small>Tracked delivery in 3–6 business days</small>
              </span>
              <strong>{shipping === 0 ? "Free" : formatCurrency(shipping)}</strong>
            </div>
          </fieldset>

          <button
            className="checkout-form__submit"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Opening secure payment…" : `Pay now · ${formatCurrency(total)}`}
          </button>

          <p className="checkout-form__legal">
            By continuing to payment, you agree to our <Link to="/terms">terms</Link>
            {" and "}
            <Link to="/privacy">privacy policy</Link>.
          </p>
        </form>

        <aside className="checkout-summary" aria-labelledby="checkout-summary-title">
          <h2 id="checkout-summary-title">In your order</h2>
          <ul className="checkout-summary__items">
            {cartLines.map((item) => {
              const productImage = item.product.image || item.product.images?.[0];
              return (
                <li key={`${item.id}-${item.size}-${item.color}`}>
                  <div className="checkout-summary__image">
                    {productImage ? (
                      <img src={productImage} alt="" />
                    ) : (
                      <span aria-hidden="true">IL</span>
                    )}
                    <span>{item.quantity}</span>
                  </div>
                  <div className="checkout-summary__item-copy">
                    <h3>{item.product.name}</h3>
                    {(item.color || item.size) && (
                      <p>{[item.color, item.size].filter(Boolean).join(" · ")}</p>
                    )}
                  </div>
                  <strong>
                    {formatCurrency(toNumber(item.product.price) * item.quantity)}
                  </strong>
                </li>
              );
            })}
          </ul>

          <dl className="checkout-summary__totals">
            <div>
              <dt>Subtotal</dt>
              <dd>{formatCurrency(subtotal)}</dd>
            </div>
            <div>
              <dt>Shipping</dt>
              <dd>{shipping === 0 ? "Free" : formatCurrency(shipping)}</dd>
            </div>
            <div className="checkout-summary__total">
              <dt>Total <small>USD</small></dt>
              <dd>{formatCurrency(total)}</dd>
            </div>
          </dl>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
